import React, { useMemo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import AssetCard from './AssetCard';
import {
  useQueryWithSimpleErrorHandling,
  useClientQueryWithSimpleErrorHandling,
  useToast,
  useDictionaryHandling,
  useParams,
  useSimpleError
} from 'hooks';
import { useMutation, useConditionalSubscription } from 'features/common/security/hooks';
import { useSubscription } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  ASSET_QUERY,
  RETRIEVE_ASSET_DATA_QUERY,
  ENGINE_TYPES_QUERY,
  REGISTRATION_TYPES_QUERY,
  ASSET_CATEGORIES_QUERY,
  ASSET_SUBCATEGORIES_QUERY,
  VEHICLE_CATEGORIES_QUERY,
  ASSET_HIERARCHY_QUERY,
  ASSET_HIERARCHY_LEVEL_SOURCE_QUERY,
  EQUIPMENT_QUERY
} from '../queries';
import { UPDATE_ASSET, RETRIEVE_ASSET_IMAGE } from '../mutations';
import { ASSET_SAVED, ASSET_IMAGE_RETRIEVED } from '../subscriptions';
import { useChangeTrackingLens, get, set, sequence } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { shape, required, when, fromModel, stopOnFirstFailure, errorMessage, maxLength, between } from '@totalsoft/pure-validations';
import {
  hasFixedLength,
  hasCapitalLettersAndNumbers,
  isMonth,
  isCIVSeries,
  isPositive,
  isPositiveOrZero,
  empty,
  manufacturingYearAssetValidator
} from 'utils/simple-validations/validations';
import { chassisNumberLength, emptyArray, engineTypeCodes, assetFieldsMaxValue } from 'constants/defaultValues';
import { Prompt } from 'react-router-dom';
import i18next from 'i18next';

const getChassisNumberValidator = fromModel(item =>
  shape({
    chassisNumber: [required, hasFixedLength(item.chassisNumber, chassisNumberLength)] |> stopOnFirstFailure
  })
);

const getAssetValidator = (
  insuranceTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  permanentRegistrationTypeId,
  validateCascoProperties
) => {
  return fromModel(item =>
    shape({
      assetTypeId: required,
      chassisNumber:
        [
          required |> when(insuranceTypeCode === rcaInsuranceTypeCode ? true : validateCascoProperties),
          hasFixedLength(item.chassisNumber, chassisNumberLength)
            |> when(insuranceTypeCode === rcaInsuranceTypeCode ? true : validateCascoProperties)
        ] |> stopOnFirstFailure,
      rcaCategoryId: required |> when(insuranceTypeCode === rcaInsuranceTypeCode),
      cascoCategoryId: required |> when(insuranceTypeCode === cascoInsuranceTypeCode),
      subcategoryId: required |> when(insuranceTypeCode === rcaInsuranceTypeCode),
      assetHierarchyId: required,
      registrationTypeId: required,
      registrationNumber:
        [
          required |> when(item.registrationTypeId === permanentRegistrationTypeId),
          hasCapitalLettersAndNumbers |> when(item.registrationTypeId === permanentRegistrationTypeId),
          maxLength(20)
        ] |> stopOnFirstFailure,
      engineType: required,
      mandatoryLevelValue:
        [
          required |> when(!item.otherAssetName) |> errorMessage(i18next.t('Validations.Asset.MandatoryLevel')),
          empty |> when(Boolean(item.otherAssetName)) |> errorMessage(i18next.t('Validations.Asset.OneOfOtherMandatoryLevel'))
        ] |> stopOnFirstFailure,
      otherAssetName:
        [
          required |> when(!item.mandatoryLevelValue || item.reverseLevel !== 0) |> errorMessage(i18next.t('Validations.Asset.Other')),
          empty |> when(Boolean(item.mandatoryLevelValue)) |> errorMessage(i18next.t('Validations.Asset.OneOfOtherMandatoryLevel'))
        ] |> stopOnFirstFailure,
      cylindricCapacity: [required, isPositiveOrZero, between(0, assetFieldsMaxValue.cylindricCapacity)] |> stopOnFirstFailure,
      enginePower: [required, isPositiveOrZero, between(0, assetFieldsMaxValue.enginePower)] |> stopOnFirstFailure,
      maxWeight: [required, isPositive, between(0, assetFieldsMaxValue.maxWeight)] |> stopOnFirstFailure,
      seatsNumber: [required, isPositiveOrZero, between(0, assetFieldsMaxValue.seatsNumber)] |> stopOnFirstFailure,
      manufacturingYear: [required, manufacturingYearAssetValidator] |> stopOnFirstFailure,
      manufacturingMonth:
        [required |> when(insuranceTypeCode === cascoInsuranceTypeCode), isMonth |> when(insuranceTypeCode === cascoInsuranceTypeCode)]
        |> stopOnFirstFailure,
      identityCardSeries:
        [required, hasCapitalLettersAndNumbers, isCIVSeries] |> stopOnFirstFailure |> when(insuranceTypeCode === rcaInsuranceTypeCode),
      currentMileage: [required, isPositiveOrZero, between(0, assetFieldsMaxValue.currentMileage)] |> stopOnFirstFailure,
      purchasePrice:
        [
          required |> when(insuranceTypeCode === cascoInsuranceTypeCode),
          isPositive |> when(insuranceTypeCode === cascoInsuranceTypeCode),
          between(0, assetFieldsMaxValue.purchasePrice)
        ] |> stopOnFirstFailure,
      purchaseInvoiceDate: required |> when(validateCascoProperties),
      firstRegistrationDate: required |> when(validateCascoProperties),
      vehicleCategoryCode: required |> when(validateCascoProperties)
    })
  );
};

const emptyAsset = {
  purchaseInvoiceDate: null,
  firstRegistrationDate: null,
  qtty: 1
};

const AssetContainer = ({
  documentId,
  assetId,
  insuranceTypeId,
  validateCascoProperties,
  insuranceTypeCode,
  currencyCode,
  offerTypeCode,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  onCreateAsset,
  saveDisabled,
  setSaveDisabled,
  saving,
  setSaving,
  onCheckActiveOffers,
  readOnly,
  dirtyInfoRef,
  setDirtyInfoRef
}) => {
  const addToast = useToast();
  const { t } = useTranslation();
  const showError = useSimpleError();
  const clientQuery = useClientQueryWithSimpleErrorHandling();
  const permanentRegistrationTypeCode = useParams('PermanentRegistrationTypeCode');
  const mandatoryCompletionLevel = useParams('AssetHierarchyMandatoryCompletionLevel');
  const offerTypeConfigurationJSON = useParams('OfferTypeCodesConfiguration');
  const {
    loading: registrationTypesLoading,
    dictionary: registrationTypesData,
    getIdByCode: getRegistrationTypeIdByCode
  } = useDictionaryHandling(REGISTRATION_TYPES_QUERY, 'registrationTypes', {
    skip: !insuranceTypeId,
    variables: { insuranceTypeId: insuranceTypeId }
  });

  const {
    loading: engineTypesLoading,
    dictionary: engineTypes,
    getCodeByName: getEngineTypeCodeByName
  } = useDictionaryHandling(ENGINE_TYPES_QUERY, 'engineTypes');

  // #region State

  const [assetLens, assetDirtyInfo, resetAssetLens] = useChangeTrackingLens(emptyAsset);
  const [equipmentLens, , resetEquipment] = useChangeTrackingLens([]);
  const [hierarchyLevels, sethierarchyLevels] = useState([]);
  const [hierarchyLevelsSources, setHierarchyLevelsSources] = useState({});
  const [equipmentDirty, setEquipmentDirty] = useState(false);
  const [isNewAsset, setIsNewAsset] = useState(true);
  const [categoryId, setCategoryId] = useState(null);
  const [searching, setSearching] = useState(false);

  // #endregion

  const offerTypeCodes = useMemo(() => {
    if (offerTypeConfigurationJSON) return JSON.parse(offerTypeConfigurationJSON);
  }, [offerTypeConfigurationJSON]);

  const searchInactive = useMemo(() => {
    return offerTypeCode === offerTypeCodes?.Cession || offerTypeCode === offerTypeCodes?.Restructuring;
  }, [offerTypeCode, offerTypeCodes]);

  const ignoreCategoryConfiguration = useMemo(() => {
    return offerTypeCode === offerTypeCodes?.Renewal;
  }, [offerTypeCode, offerTypeCodes]);

  const permanentRegistrationTypeId = useMemo(
    () => getRegistrationTypeIdByCode(permanentRegistrationTypeCode),
    [permanentRegistrationTypeCode, getRegistrationTypeIdByCode]
  );

  // #region Validation

  const assetDataValidator = useMemo(
    () =>
      getAssetValidator(
        insuranceTypeCode,
        rcaInsuranceTypeCode,
        cascoInsuranceTypeCode,
        permanentRegistrationTypeId,
        validateCascoProperties
      ),
    [insuranceTypeCode, rcaInsuranceTypeCode, cascoInsuranceTypeCode, permanentRegistrationTypeId, validateCascoProperties]
  );
  const [validation, validate] = useDirtyFieldValidation(assetDataValidator);

  const validatorChassisNumber = getChassisNumberValidator;
  const [, validateChassisNumber] = useDirtyFieldValidation(validatorChassisNumber);

  // #endregion

  const fetchHierarchyLevelsSources = useCallback(
    async hierarchyIds => {
      if (!hierarchyIds) return;
      const levelSources = {};
      for (const hierarhyId of hierarchyIds) {
        if (hierarhyId) {
          const { data } = await clientQuery(ASSET_HIERARCHY_LEVEL_SOURCE_QUERY, {
            variables: { parentId: hierarhyId }
          });
          if (data?.assetHierarchyLevelSource) {
            levelSources[data.assetHierarchyLevelSource.level] = data.assetHierarchyLevelSource;
          }
        }
      }
      setHierarchyLevelsSources(prev => ({
        ...prev,
        ...levelSources
      }));
    },
    [clientQuery]
  );

  const getParentHierarchyLevelIds = useCallback(hierarchy => {
    if (!Array.isArray(hierarchy)) return [];
    return hierarchy.filter(x => x.reverseLevel !== 0).map(x => x.id);
  }, []);

  // #region Queries

  const { loading: assetTypesLoading, data: assetTypesData } = useQueryWithSimpleErrorHandling(ASSET_HIERARCHY_LEVEL_SOURCE_QUERY, {
    variables: { parentId: null }
  });

  const assetTypeCode = useMemo(() => {
    return assetTypesData?.assetHierarchyLevelSource.data.find(x => x.id === (assetLens.assetTypeId |> get))?.code;
  }, [assetLens.assetTypeId, assetTypesData]);

  const {
    loading: hierarchyLevelsLoading,
    data: hierarchyLevelsData,
    refetch: refetchHierarchyLevels
  } = useQueryWithSimpleErrorHandling(ASSET_HIERARCHY_QUERY, {
    variables: { assetTypeId: assetLens.assetTypeId |> get, searchInactive }
  });

  const { loading: categoriesLoading, data: assetCategoryData } = useQueryWithSimpleErrorHandling(ASSET_CATEGORIES_QUERY, {
    skip: !insuranceTypeCode || (!ignoreCategoryConfiguration && !assetTypeCode),
    variables: {
      insuranceTypeCode: insuranceTypeCode,
      assetTypeCode: ignoreCategoryConfiguration ? null : assetTypeCode
    }
  });

  const { loading: subcategoriesLoading, data: assetSubcategoryData } = useQueryWithSimpleErrorHandling(ASSET_SUBCATEGORIES_QUERY, {
    skip: insuranceTypeCode !== rcaInsuranceTypeCode || (!ignoreCategoryConfiguration && !(assetLens.rcaCategoryId |> get)),
    variables: { categoryId: ignoreCategoryConfiguration ? null : assetLens.rcaCategoryId |> get }
  });

  const { loading: vehicleCategoriesLoading, data: vehicleCategoriesData } = useQueryWithSimpleErrorHandling(VEHICLE_CATEGORIES_QUERY);

  const { loading: assetLoading } = useQueryWithSimpleErrorHandling(ASSET_QUERY, {
    variables: { assetId: assetId, searchInactive: true },
    skip: !assetId,
    onCompleted: async data => {
      if (data && data.asset) {
        const hierarchy = data.asset.hierarchy;
        resetAssetLens({
          ...(assetLens |> get),
          ...data.asset
        });

        if (Array.isArray(hierarchy) && hierarchy.length > 0) {
          await fetchHierarchyLevelsSources(getParentHierarchyLevelIds(hierarchy));
        } else {
          await fetchHierarchyLevelsSources([data.asset.assetTypeId]);
        }
        setIsNewAsset(!data.asset.chassisNumber);
        setCategoryId(insuranceTypeCode === rcaInsuranceTypeCode ? data.asset.rcaCategoryId : data.asset.cascoCategoryId);
      }
    },
    notifyOnNetworkStatusChange: true
  });

  const { loading: equipmentLoading, data: equipmentData } = useQueryWithSimpleErrorHandling(EQUIPMENT_QUERY);

  // #endregion

  // #region Mutations

  const [saveAsset] = useMutation(UPDATE_ASSET, {
    onError: error => {
      showError(error);
      setSaving(false);
    }
  });

  const [retrieveAssetImage] = useMutation(RETRIEVE_ASSET_IMAGE, {
    onError: error => {
      showError(error);
    }
  });

  // #endregion

  // #region Subscriptions

  useSubscription(ASSET_SAVED, {
    variables: { documentId },
    onData: ({ data: { data } }) => {
      if (data?.assetSaved) {
        resetAssetLens({ ...(assetLens |> get), ...data.assetSaved.asset });
        const { silentMode } = data.assetSaved;
        if (!data.assetSaved.isNew && !silentMode) addToast(t('General.Saved'), 'success', { toastId: 'assetSavedToast' });
        setIsNewAsset(data.assetSaved.isNew);
        setCategoryId(
          insuranceTypeCode === rcaInsuranceTypeCode ? data.assetSaved.asset.rcaCategoryId : data.assetSaved.asset.cascoCategoryId
        );
      }
      setEquipmentDirty(false);
      setSaving(false);
    }
  });

  useConditionalSubscription(ASSET_IMAGE_RETRIEVED, {
    onData: ({ data: { data } }) => {
      if (data?.assetImageRetrieved?.imageUrl) {
        (assetLens.imageUrl |> set)(data?.assetImageRetrieved?.imageUrl);
      }
    }
  });

  // #endregion

  const categoryName = useMemo(() => {
    return assetCategoryData?.assetCategories?.find(x => x.id === categoryId)?.name;
  }, [assetCategoryData, categoryId]);

  const description = useMemo(() => assetLens.description |> get, [assetLens.description]);

  const summaryData = useMemo(() => {
    return {
      categoryName,
      description
    };
  }, [categoryName, description]);

  const getLoadingStatus = useCallback(() => {
    return {
      assetLoading,
      engineTypesLoading,
      assetTypesLoading,
      registrationTypesLoading,
      hierarchyLevelsLoading,
      categoriesLoading,
      subcategoriesLoading,
      vehicleCategoriesLoading,
      equipmentLoading,
      summaryLoading: assetLoading || categoriesLoading
    };
  }, [
    assetLoading,
    assetTypesLoading,
    engineTypesLoading,
    registrationTypesLoading,
    hierarchyLevelsLoading,
    categoriesLoading,
    subcategoriesLoading,
    vehicleCategoriesLoading,
    equipmentLoading
  ]);

  const saveDisabledStatus = useMemo(() => {
    return saving || saveDisabled || getLoadingStatus().assetLoading || searching;
  }, [saving, saveDisabled, getLoadingStatus, searching]);

  const validateSeatsNumber = useCallback((category, seatsNumber) => {
    if (!category) return true;
    // minSeatsNumber & maxSeatsNumber could be null or undefined
    return (
      (category.minSeatsNumber == null || (seatsNumber && category.minSeatsNumber <= seatsNumber)) &&
      (category.maxSeatsNumber == null || (seatsNumber && category.maxSeatsNumber >= seatsNumber))
    );
  }, []);

  const validateWeight = useCallback((category, weight) => {
    if (!category) return true;
    return (
      // minWeight & maxWeight could be null or undefined
      (category.minWeight == null || (weight && category.minWeight <= weight)) &&
      (category.maxWeight == null || (weight && category.maxWeight >= weight))
    );
  }, []);

  const validateCategoryOrSubcategory = useCallback(
    (category, weight, seatsNumber, checkConditions) => {
      if (!category) return true;

      // if no condition is configured, the category/subcategory will not be automatically set
      if (
        checkConditions &&
        category.minSeatsNumber == null &&
        category.maxSeatsNumber == null &&
        category.minWeight == null &&
        category.maxWeight == null
      )
        return false;
      return validateWeight(category, weight) && validateSeatsNumber(category, seatsNumber);
    },
    [validateWeight, validateSeatsNumber]
  );

  const validateSubcategory = useCallback(
    async (categoryId, weight, seatsNumber) => {
      const { data } = await clientQuery(ASSET_SUBCATEGORIES_QUERY, {
        skip: !categoryId,
        variables: { categoryId }
      });

      const currentSubcategory = data.assetSubcategories.find(x => x.id === (assetLens.subcategoryId |> get));
      if (!currentSubcategory) return;
      if (!validateCategoryOrSubcategory(currentSubcategory, weight, seatsNumber, false)) (assetLens.subcategoryId |> set)(null);
    },
    [assetLens.subcategoryId, clientQuery, validateCategoryOrSubcategory]
  );

  const mergeHierarchyLevels = useCallback((hierarchy, defaultHierarchy) => {
    const mergedHierarchy = [];

    for (let level = 1; ; level++) {
      const hierarchyLevel = hierarchy.find(x => x.level === level);
      if (hierarchyLevel) mergedHierarchy.push(hierarchyLevel);
      else {
        const defaultHierarchyLevel = defaultHierarchy.find(x => x.level === level);
        if (defaultHierarchyLevel) mergedHierarchy.push(defaultHierarchyLevel);
        else break;
      }
    }
    return mergedHierarchy;
  }, []);

  const resetAssetHierarchy = useCallback(
    async assetTypeId => {
      refetchHierarchyLevels({ assetTypeId });

      if (assetTypeId) {
        const { data } = await clientQuery(ASSET_HIERARCHY_LEVEL_SOURCE_QUERY, {
          variables: { parentId: assetTypeId }
        });

        if (data?.assetHierarchyLevelSource) {
          setHierarchyLevelsSources(prev => ({
            ...prev,
            [data.assetHierarchyLevelSource.level]: data.assetHierarchyLevelSource
          }));
        }
      }
    },
    [clientQuery, refetchHierarchyLevels]
  );
  // #region Handlers

  const getDefaultSubcategoryId = useCallback(
    async (categoryId, weight, seatsNumber) => {
      const { data } = await clientQuery(ASSET_SUBCATEGORIES_QUERY, {
        variables: { categoryId }
      });
      const defaultSubcategory = data?.assetSubcategories.find(
        x => validateCategoryOrSubcategory(x, weight, seatsNumber, false) && x.categoryId === categoryId
      );
      if (defaultSubcategory) return defaultSubcategory.id;
      else return null;
    },
    [clientQuery, validateCategoryOrSubcategory]
  );

  const computeSubcategory = useCallback(
    async (categoryId, weight, seatsNumber) => {
      let validSubcategory = false;
      let defaultSubcategoryId = null;
      if (categoryId) defaultSubcategoryId = await getDefaultSubcategoryId(categoryId, weight, seatsNumber);
      if (defaultSubcategoryId) return defaultSubcategoryId;
      if (assetLens.subcategoryId |> get) validSubcategory = await validateSubcategory(categoryId, weight, seatsNumber);
      if (validSubcategory) return assetLens.subcategoryId |> get;
      return null;
    },
    [assetLens.subcategoryId, getDefaultSubcategoryId, validateSubcategory]
  );

  const handleCategoryChange = useCallback(
    async value => {
      let validSubcategory = true;
      if (!ignoreCategoryConfiguration && insuranceTypeCode === rcaInsuranceTypeCode && (assetLens.subcategoryId |> get))
        validSubcategory = await validateSubcategory(value, assetLens.weight |> get, assetLens.seatsNumber |> get);
      const subcategoryId = validSubcategory ? assetLens.subcategoryId |> get : null;
      if (insuranceTypeCode === rcaInsuranceTypeCode)
        (assetLens |> set)({
          ...(assetLens |> get),
          rcaCategoryId: value,
          subcategoryId: subcategoryId
        });
      else (assetLens.cascoCategoryId |> set)(value);
      setCategoryId(value);
    },
    [assetLens, insuranceTypeCode, rcaInsuranceTypeCode, ignoreCategoryConfiguration, validateSubcategory]
  );

  const computeCategory = useCallback(
    async (assetTypeId, weight, seatsNumber) => {
      if (categoryId && ignoreCategoryConfiguration) return categoryId;

      const assetTypeCode = assetTypesData?.assetHierarchyLevelSource.data.find(x => x.id === assetTypeId)?.code;
      const { data } = await clientQuery(ASSET_CATEGORIES_QUERY, {
        skip: !assetTypeCode,
        variables: { insuranceTypeCode, assetTypeCode },
        fetchPolicy: 'network-only'
        // making sure data is refetched, because ASSET_CATEGORIES_QUERY writes AssetCategory objects in cache (and the same id can be returned with different properties depending on AssetType or InsuranceType)
      });

      if (categoryId) {
        let validCategory = false;
        const currentCategory = data.assetCategories.find(x => x.id === categoryId);
        if (currentCategory) validCategory = validateCategoryOrSubcategory(currentCategory, weight, seatsNumber, false);

        if (validCategory) return categoryId;
      }

      let defaultCategory = data.assetCategories.find(x => x.implicit === true);
      if (!defaultCategory) defaultCategory = data.assetCategories.find(x => validateCategoryOrSubcategory(x, weight, seatsNumber, true));
      if (defaultCategory) return defaultCategory.id;
      else return null;
    },
    [ignoreCategoryConfiguration, clientQuery, insuranceTypeCode, assetTypesData, categoryId, validateCategoryOrSubcategory]
  );

  const getAssetCategoryData = useCallback(
    async (assetTypeId, maxWeight, seatsNumber) => {
      const categoryId = await computeCategory(assetTypeId, maxWeight, seatsNumber);

      // keep existing rca category on casco offers and viceversa
      const rcaCategoryId = insuranceTypeCode === rcaInsuranceTypeCode ? categoryId : assetLens.rcaCategoryId |> get;
      const cascoCategoryId = insuranceTypeCode === cascoInsuranceTypeCode ? categoryId : assetLens.cascoCategoryId |> get;
      let subcategoryId = assetLens.subcategoryId |> get;

      if (insuranceTypeCode === rcaInsuranceTypeCode) {
        if (categoryId) subcategoryId = await computeSubcategory(categoryId, maxWeight, seatsNumber);
        else subcategoryId = null;
      }
      return {
        categoryId,
        rcaCategoryId,
        cascoCategoryId,
        subcategoryId
      };
    },
    [
      assetLens.rcaCategoryId,
      assetLens.cascoCategoryId,
      assetLens.subcategoryId,
      insuranceTypeCode,
      rcaInsuranceTypeCode,
      cascoInsuranceTypeCode,
      computeCategory,
      computeSubcategory
    ]
  );

  const handleAssetTypeChange = useCallback(
    async value => {
      const assetCategoryData = await getAssetCategoryData(value, assetLens.maxWeight |> get, assetLens.seatsNumber |> get);

      (assetLens |> set)({
        ...(assetLens |> get),
        assetTypeId: value,
        rcaCategoryId: assetCategoryData.rcaCategoryId,
        cascoCategoryId: assetCategoryData.cascoCategoryId,
        subcategoryId: assetCategoryData.subcategoryId,
        assetHierarchyId: null,
        reverseLevel: null,
        otherAssetName: null,
        hierarchy: null,
        imageUrl: null
      });
      setCategoryId(assetCategoryData.categoryId);
      resetAssetHierarchy(value);
    },
    [assetLens, getAssetCategoryData, resetAssetHierarchy]
  );

  /**
   * Handles the logic of a hierarchy level change
   * @param {*} levelData contains the new values
   * @param {*} level contains current hierarchy values
   */
  const handleHierarchyLevelChange = useCallback(
    async (levelData, level) => {
      if (levelData === null) {
        //is delete
        levelData = { id: null, name: null };
      } else {
        const currentLevel = hierarchyLevels.find(x => x.level === level.level);
        if (currentLevel && currentLevel.id === levelData.id) return;
      }

      const lowerLevels = hierarchyLevels.filter(z => z.level < level.level);
      let upperLevels = hierarchyLevels.filter(z => z.level > level.level);

      //invalidate upper levels
      upperLevels = upperLevels.map(z => ({
        ...z,
        id: null,
        name: null
      }));

      const { id, name, reverseLevel } = levelData;
      const newHierarchyLevels = [...lowerLevels, { ...level, id, name, reverseLevel }, ...upperLevels];

      (assetLens.hierarchy |> set)(newHierarchyLevels);

      const maxLevel = Math.max.apply(
        Math,
        hierarchyLevels.map(x => x.level)
      );

      if (levelData.id) {
        (assetLens.assetHierarchyId |> set)(levelData.id);
        (assetLens.reverseLevel |> set)(levelData.reverseLevel);
      } else {
        (assetLens.assetHierarchyId |> set)(newHierarchyLevels.find(x => x.level === level.level - 1)?.id);
        (assetLens.reverseLevel |> set)(newHierarchyLevels.find(x => x.level === level.level - 1)?.reverseLevel);
      }
      (assetLens.imageUrl |> set)(null);
      if (level.level === mandatoryCompletionLevel) {
        (assetLens.mandatoryLevelValue |> set)(levelData.id);
        /* Clear otherAssetName only if the hierarchy value was empty before and therefore a value was added to it. Refer to [handleOtherAssetNameChange] */
        if (levelData.id && !level.id) (assetLens.otherAssetName |> set)(null);
      }

      if (id) {
        const { data } = await clientQuery(ASSET_HIERARCHY_LEVEL_SOURCE_QUERY, {
          variables: { parentId: id }
        });

        if (data?.assetHierarchyLevelSource) {
          setHierarchyLevelsSources(prev => ({
            ...prev,
            [data.assetHierarchyLevelSource.level]: data.assetHierarchyLevelSource
          }));
        }
      }

      if (level.level === maxLevel) {
        if (levelData.imageUrl) {
          (assetLens.imageUrl |> set)(levelData.imageUrl);
        } else if (levelData.id) {
          retrieveAssetImage({
            variables: {
              input: {
                assetHierarchyId: levelData.id
              }
            }
          });
        }
      }
    },
    [assetLens, clientQuery, hierarchyLevels, mandatoryCompletionLevel, retrieveAssetImage]
  );

  const handleOtherAssetNameChange = useCallback(
    value => {
      const previousValue = assetLens.otherAssetName |> get;
      (assetLens.otherAssetName |> set)(value);
      /* Only reset the hierarchy if otherAssetName was empty before and therefore a value was added to it. This is done 
       for the case of an asset retrieved by chassisNumber where both otherAssetName and the mandatory level in hierarchy 
       were completed => otherAssetName has a value so the user can remove it without affecting the hierarchy. */
      if (!previousValue) {
        const mandatoryLevel = (assetLens.hierarchy |> get)?.find(x => x.level === mandatoryCompletionLevel);
        if (mandatoryLevel && mandatoryLevel.id) handleHierarchyLevelChange(null, mandatoryLevel);
      }
    },
    [assetLens, handleHierarchyLevelChange, mandatoryCompletionLevel]
  );

  const handleChassisNumberChange = useCallback(
    async chassisNumber => {
      (assetLens.chassisNumber |> set)(chassisNumber);
      if (chassisNumber === null) return;
      if (!validateChassisNumber({ chassisNumber })) return;
      await onCheckActiveOffers(chassisNumber);

      setSearching(true);

      const { data } = await clientQuery(RETRIEVE_ASSET_DATA_QUERY, {
        variables: { chassisNumber, insuranceTypeId },
        fetchPolicy: 'network-only'
      });

      if (data && data.retrieveAssetData) {
        if (data.retrieveAssetData.timeOut) addToast(t('Offer.Edit.Asset.SearchTimeout'), 'warning', { toastId: 'searchTimeoutToast' });

        const assetId = assetLens.assetId |> get;
        const hierarchy = data.retrieveAssetData.assetData.hierarchy;

        setEquipmentDirty(false);

        resetAssetLens({
          ...data.retrieveAssetData.assetData,
          mandatoryLevelValue: Array.isArray(hierarchy) ? hierarchy.find(x => x.level === mandatoryCompletionLevel)?.id : null,
          reverseLevel: Array.isArray(hierarchy)
            ? hierarchy.find(x => x.id === data.retrieveAssetData.assetData.assetHierarchyId)?.reverseLevel
            : null
        });

        if (Array.isArray(hierarchy) && hierarchy.length > 0) {
          await fetchHierarchyLevelsSources(getParentHierarchyLevelIds(hierarchy));
        } else {
          await fetchHierarchyLevelsSources([data.retrieveAssetData.assetData.assetTypeId]);
        }
        if (data.retrieveAssetData.assetId) {
          setCategoryId(
            insuranceTypeCode === rcaInsuranceTypeCode
              ? data.retrieveAssetData.assetData.rcaCategoryId
              : data.retrieveAssetData.assetData.cascoCategoryId
          );
        }
        if (isNewAsset) (assetLens.assetId |> set)(assetId);
        else (assetLens.assetId |> set)(null);

        setSaveDisabled(false);
      }
      setSearching(false);
    },
    [
      addToast,
      assetLens,
      clientQuery,
      fetchHierarchyLevelsSources,
      getParentHierarchyLevelIds,
      insuranceTypeCode,
      insuranceTypeId,
      isNewAsset,
      mandatoryCompletionLevel,
      onCheckActiveOffers,
      rcaInsuranceTypeCode,
      resetAssetLens,
      setSaveDisabled,
      t,
      validateChassisNumber
    ]
  );

  const handleAssetPropertyBlur = useCallback(async () => {
    const assetCategoryData = await getAssetCategoryData(
      assetLens.assetTypeId |> get,
      assetLens.maxWeight |> get,
      assetLens.seatsNumber |> get
    );

    (assetLens |> set)({
      ...(assetLens |> get),
      rcaCategoryId: assetCategoryData.rcaCategoryId,
      cascoCategoryId: assetCategoryData.cascoCategoryId,
      subcategoryId: assetCategoryData.subcategoryId
    });
    setCategoryId(assetCategoryData.categoryId);
  }, [assetLens, getAssetCategoryData]);

  const handleSave = useCallback(() => {
    if (!validate(assetLens |> get)) {
      return;
    }

    setSaving(true);

    const assetId = assetLens.assetId |> get;

    const asset = {
      chassisNumber: assetLens.chassisNumber |> get,
      vehicleCategoryCode: assetLens.vehicleCategoryCode |> get,
      rcaCategoryId: assetLens.rcaCategoryId |> get,
      cascoCategoryId: assetLens.cascoCategoryId |> get,
      subcategoryId: assetLens.subcategoryId |> get,
      assetHierarchyId: assetLens.assetHierarchyId |> get,
      registrationTypeId: assetLens.registrationTypeId |> get,
      registrationNumber: assetLens.registrationNumber |> get,
      engineType: assetLens.engineType |> get,
      otherAssetName: assetLens.otherAssetName |> get,
      cylindricCapacity: assetLens.cylindricCapacity |> get,
      enginePower: assetLens.enginePower |> get,
      maxWeight: assetLens.maxWeight |> get,
      seatsNumber: assetLens.seatsNumber |> get,
      manufacturingYear: assetLens.manufacturingYear |> get,
      manufacturingMonth: assetLens.manufacturingMonth |> get,
      identityCardSeries: assetLens.identityCardSeries |> get,
      currentMileage: assetLens.currentMileage |> get,
      purchasePrice: assetLens.purchasePrice |> get,
      purchaseInvoiceDate: assetLens.purchaseInvoiceDate |> get,
      firstRegistrationDate: assetLens.firstRegistrationDate |> get,
      equipment: (equipmentLens |> get).filter(equipment => equipment.selected).map(equipment => equipment.id)
    };

    if (assetId) {
      saveAsset({
        variables: {
          input: {
            documentId: documentId,
            assetId: assetId,
            insuranceTypeCode: insuranceTypeCode,
            asset: asset,
            validateCascoProperties: validateCascoProperties
          }
        }
      });
    } else onCreateAsset(asset);
  }, [assetLens, documentId, equipmentLens, insuranceTypeCode, onCreateAsset, saveAsset, setSaving, validate, validateCascoProperties]);

  const handleSelectEquipment = useCallback(
    idx => prop => value => {
      setEquipmentDirty(true);
      (equipmentLens |> sequence).forEach((item, index) => index === idx && (item[prop] |> set)(value));
    },
    [equipmentLens]
  );

  // #endregion

  const getReadOnly = useCallback(
    (controlName, hasAuthorization) => {
      return readOnly({ customValidationFunction: () => !searching, controlName, hasAuthorization });
    },
    [readOnly, searching]
  );

  // #region Side effects

  useEffect(() => {
    if (!assetLoading && !hierarchyLevelsLoading) {
      const hierarchy = assetLens.hierarchy |> get;
      const defaultHierarchy = hierarchyLevelsData?.assetHierarchy || [];
      if (!Array.isArray(hierarchy) || hierarchy.length <= 0) {
        sethierarchyLevels(defaultHierarchy);
      } else {
        sethierarchyLevels(mergeHierarchyLevels(hierarchy, defaultHierarchy));
      }
    }
  }, [assetLoading, hierarchyLevelsLoading, assetLens.hierarchy, hierarchyLevelsData, mergeHierarchyLevels]);

  useEffect(() => {
    if (!assetLoading && !isDirty(assetDirtyInfo) && !(assetLens.mandatoryLevelValue |> get)) {
      const hierarchy = assetLens.hierarchy |> get;
      if (Array.isArray(hierarchy) && hierarchy.some(x => x.level === mandatoryCompletionLevel)) {
        resetAssetLens(prev => ({
          ...prev,
          mandatoryLevelValue: hierarchy.find(x => x.level === mandatoryCompletionLevel)?.id,
          reverseLevel: hierarchy.find(x => x.id === prev.assetHierarchyId)?.reverseLevel
        }));
      }
    }
  }, [assetLoading, assetLens, assetDirtyInfo, mandatoryCompletionLevel, resetAssetLens]);

  useEffect(() => {
    const unsavedChanges = isDirty(assetDirtyInfo) || equipmentDirty;
    setSaveDisabled(!unsavedChanges);

    setDirtyInfoRef('assetData', unsavedChanges);
    const { onDirtyInfoChange } = dirtyInfoRef.current;
    onDirtyInfoChange && onDirtyInfoChange();
  }, [setSaveDisabled, assetDirtyInfo, equipmentDirty, dirtyInfoRef, setDirtyInfoRef]);

  useEffect(() => {
    validate(assetLens |> get, assetDirtyInfo);
  }, [validate, assetLens, assetDirtyInfo]);

  useEffect(() => {
    const selectedEquipment = assetLens.equipment |> get;

    if (!selectedEquipment || !equipmentData?.equipment || equipmentDirty) return;
    let list = [];

    equipmentData?.equipment?.forEach(equipmentItem => {
      const isSelected = selectedEquipment.find(x => x === equipmentItem.id);
      list.push({ id: equipmentItem.id, name: equipmentItem.name, selected: isSelected });
    });
    resetEquipment([...list]);
  }, [equipmentData, equipmentDirty, resetEquipment, assetLens.equipment]);

  const engineType = assetLens.engineType |> get;
  useEffect(() => {
    if (!engineType) return;
    const code = getEngineTypeCodeByName(engineType);
    const cylindricalCapacity = assetLens.cylindricCapacity |> get;
    if (code == engineTypeCodes.ELECTRIC && cylindricalCapacity != 0) {
      (assetLens.cylindricCapacity |> set)(0);
    }
  }, [engineType, getEngineTypeCodeByName, assetLens.cylindricCapacity]);
  // #endregion

  return (
    <>
      <Prompt when={isDirty(assetDirtyInfo)} message={t('LeavingWithoutSaving')} />
      <AssetCard
        loadingStatus={getLoadingStatus()}
        assetLens={assetLens}
        summaryData={summaryData}
        insuranceTypeCode={insuranceTypeCode}
        rcaInsuranceTypeCode={rcaInsuranceTypeCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeCode}
        currencyCode={currencyCode}
        engineTypes={{ dictionary: engineTypes, getEngineTypeCodeByName }}
        assetTypes={assetTypesData?.assetHierarchyLevelSource.data}
        registrationTypes={registrationTypesData}
        assetCategories={assetCategoryData?.assetCategories}
        assetSubcategories={assetSubcategoryData?.assetSubcategories}
        vehicleCategories={vehicleCategoriesData?.vehicleCategories}
        hierarchy={hierarchyLevels || emptyArray}
        hierarchyLevelsSources={hierarchyLevelsSources}
        onCategoryChange={handleCategoryChange}
        onAssetTypeChange={handleAssetTypeChange}
        onHierarchyLevelChange={handleHierarchyLevelChange}
        onOtherAssetNameChange={handleOtherAssetNameChange}
        onChassisNumberChange={handleChassisNumberChange}
        onAssetPropertyBlur={handleAssetPropertyBlur}
        onSave={handleSave}
        saveDisabled={saveDisabledStatus}
        saving={saving}
        validation={validation}
        equipmentLens={equipmentLens}
        onSelectEquipment={handleSelectEquipment}
        validateCascoProperties={validateCascoProperties}
        readOnly={getReadOnly}
      />
    </>
  );
};

AssetContainer.propTypes = {
  documentId: PropTypes.number.isRequired,
  assetId: PropTypes.number,
  insuranceTypeId: PropTypes.number,
  insuranceTypeCode: PropTypes.string,
  currencyCode: PropTypes.string,
  offerTypeCode: PropTypes.string,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  onCreateAsset: PropTypes.func.isRequired,
  saveDisabled: PropTypes.bool.isRequired,
  setSaveDisabled: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  setSaving: PropTypes.func.isRequired,
  onCheckActiveOffers: PropTypes.func.isRequired,
  validateCascoProperties: PropTypes.bool.isRequired,
  readOnly: PropTypes.func.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};

export default AssetContainer;
